import {Component, EventEmitter, Injectable, Input, NgModule, Output} from '@angular/core';

export class PageEvent {
  pageIndex = 0;
  pageSize = 10;
  length = 0;
  previousPageIndex?: number;
}

@Injectable({providedIn: 'root'})
export class SdPaginatorIntl {
  itemsPerPageLabel = 'Items per page';
  nextPageLabel = 'Next page';
  previousPageLabel = 'Previous page';
  firstPageLabel = 'First page';
  lastPageLabel = 'Last page';
  changes = new EventEmitter<void>();

  getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return `0 of ${length}`;
    }
    const start = page * pageSize;
    const end = Math.min(start + pageSize, length);
    return `${start + 1} – ${end} of ${length}`;
  };
}

@Component({
  selector: 'sd-paginator',
  exportAs: 'sdPaginator',
  standalone: true,
  template: `
    <div class="flex flex-wrap items-center justify-end gap-3 px-3 py-2 text-sm text-[var(--m365-text-secondary,#6b6b70)]">
      <label class="flex items-center gap-2">
        <span>{{ intl.itemsPerPageLabel }}</span>
        <select
          class="rounded-md border border-[var(--m365-border-rest,#ececf0)] bg-white px-2 py-1"
          [value]="pageSize"
          (change)="onPageSize($event)"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size">{{ size }}</option>
          }
        </select>
      </label>
      <span>{{ rangeLabel }}</span>
      <div class="flex items-center gap-1">
        @if (showFirstLastButtons) {
          <button type="button" class="rounded p-1 hover:bg-black/[0.04] disabled:opacity-40" [disabled]="pageIndex === 0" (click)="go(0)">«</button>
        }
        <button type="button" class="rounded p-1 hover:bg-black/[0.04] disabled:opacity-40" [disabled]="pageIndex === 0" (click)="go(pageIndex - 1)">‹</button>
        <button type="button" class="rounded p-1 hover:bg-black/[0.04] disabled:opacity-40" [disabled]="pageIndex >= lastPage" (click)="go(pageIndex + 1)">›</button>
        @if (showFirstLastButtons) {
          <button type="button" class="rounded p-1 hover:bg-black/[0.04] disabled:opacity-40" [disabled]="pageIndex >= lastPage" (click)="go(lastPage)">»</button>
        }
      </div>
    </div>
  `,
  host: {class: 'sd-paginator sd-paginator block w-full'},
})
export class SdPaginator {
  @Input() length = 0;
  @Input() pageIndex = 0;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];
  @Input() showFirstLastButtons = false;
  @Input() hidePageSize = false;
  @Input() disabled = false;
  @Output() page = new EventEmitter<PageEvent>();
  @Output() pageIndexChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  constructor(public intl: SdPaginatorIntl) {}

  get lastPage(): number {
    return Math.max(0, Math.ceil(this.length / (this.pageSize || 1)) - 1);
  }

  get rangeLabel(): string {
    return this.intl.getRangeLabel(this.pageIndex, this.pageSize, this.length);
  }

  go(index: number): void {
    const previousPageIndex = this.pageIndex;
    this.pageIndex = Math.min(this.lastPage, Math.max(0, index));
    this.emit(previousPageIndex);
  }

  firstPage(): void {
    this.go(0);
  }

  lastPageNav(): void {
    this.go(this.lastPage);
  }

  onPageSize(event: Event): void {
    const previousPageIndex = this.pageIndex;
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.pageIndex = 0;
    this.pageSizeChange.emit(this.pageSize);
    this.emit(previousPageIndex);
  }

  private emit(previousPageIndex: number): void {
    this.pageIndexChange.emit(this.pageIndex);
    this.page.emit({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length,
      previousPageIndex,
    });
  }
}

@NgModule({
  imports: [SdPaginator],
  exports: [SdPaginator],
  providers: [SdPaginatorIntl],
})
export class SdPaginatorModule {}
